// Asset utility functions — pure helpers with no side effects
// Implementation will be added after tests are written

import type { Owner } from "../data/types";

// ── Types ───────────────────────────────────────────────────────

export type AssetCategory = "branding" | "documents" | "decks" | "notes" | "images" | "other";

export interface Asset {
  id: string;
  title: string;
  description: string;
  category: AssetCategory;
  owner: Owner;
  storageId?: string;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  fileUrl?: string | null;
  content?: string;          // inline text content for notes/docs
  folderId?: string | null;   // null/undefined = root
  createdAt: number;
  updatedAt: number;
}

export interface AssetVersion {
  id: string;
  assetId: string;
  storageId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedBy: Owner;
  createdAt: number;
  versionNumber: number;
}

export interface AssetFilterState {
  category: AssetCategory | "all";
  owner: Owner | "all";
  search: string;
}

// ── Constants ───────────────────────────────────────────────────

export const ASSET_CATEGORIES: { value: AssetCategory; label: string }[] = [
  { value: "branding", label: "Branding" },
  { value: "documents", label: "Documents" },
  { value: "decks", label: "Decks" },
  { value: "notes", label: "Notes" },
  { value: "images", label: "Images" },
  { value: "other", label: "Other" },
];

export const DEFAULT_ASSET_FILTERS: AssetFilterState = {
  category: "all",
  owner: "all",
  search: "",
};

// ── Filter ──────────────────────────────────────────────────────

export function filterAssets(assets: Asset[], filters: AssetFilterState): Asset[] {
  return assets.filter((asset) => {
    if (filters.category !== "all" && asset.category !== filters.category) return false;
    if (filters.owner !== "all" && asset.owner !== filters.owner) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const matchesTitle = asset.title.toLowerCase().includes(q);
      const matchesDescription = asset.description.toLowerCase().includes(q);
      const matchesFileName = asset.fileName?.toLowerCase().includes(q) ?? false;
      if (!matchesTitle && !matchesDescription && !matchesFileName) return false;
    }
    return true;
  });
}

// ── File type detection ─────────────────────────────────────────

export function isPreviewableImage(fileType: string | undefined): boolean {
  if (!fileType) return false;
  return /^image\/(png|jpe?g|gif|webp|svg\+xml|avif)$/i.test(fileType);
}

export function isPreviewablePdf(fileType: string | undefined): boolean {
  if (!fileType) return false;
  return fileType === "application/pdf";
}

export function isPreviewable(fileType: string | undefined): boolean {
  return isPreviewableImage(fileType) || isPreviewablePdf(fileType);
}

export function getFileExtension(fileName: string | undefined): string {
  if (!fileName) return "";
  const parts = fileName.split(".");
  if (parts.length < 2) return "";
  return parts[parts.length - 1]!.toLowerCase();
}

// ── Formatting ──────────────────────────────────────────────────

export function formatFileSize(bytes: number | undefined): string {
  if (bytes === undefined || bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let unitIndex = 0;
  let size = bytes;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size % 1 === 0 ? size : size.toFixed(1)} ${units[unitIndex]}`;
}

export function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;

  return new Date(timestamp).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

// ── Category helpers ────────────────────────────────────────────

export function getCategoryLabel(category: AssetCategory): string {
  return ASSET_CATEGORIES.find((c) => c.value === category)?.label ?? "Other";
}

export function inferCategoryFromFileType(fileType: string): AssetCategory {
  if (/svg/i.test(fileType)) return "branding";
  if (/^image\//i.test(fileType)) return "images";
  if (fileType === "application/pdf") return "documents";
  if (/presentation|powerpoint|pptx?/i.test(fileType)) return "decks";
  if (/text\/plain|text\/markdown/i.test(fileType)) return "notes";
  return "other";
}

// ── Sorting ─────────────────────────────────────────────────────

export type AssetSortField = "title" | "updatedAt" | "createdAt" | "fileSize";

export function sortAssets(assets: Asset[], field: AssetSortField, direction: "asc" | "desc" = "desc"): Asset[] {
  const sorted = [...assets].sort((a, b) => {
    switch (field) {
      case "title":
        return a.title.localeCompare(b.title);
      case "updatedAt":
        return a.updatedAt - b.updatedAt;
      case "createdAt":
        return a.createdAt - b.createdAt;
      case "fileSize":
        return (a.fileSize ?? 0) - (b.fileSize ?? 0);
      default:
        return 0;
    }
  });
  return direction === "desc" ? sorted.reverse() : sorted;
}

// ── Stats ───────────────────────────────────────────────────────

export interface AssetStats {
  total: number;
  byCategory: Record<AssetCategory, number>;
  totalSizeBytes: number;
  withFile: number;
}

export function computeAssetStats(assets: Asset[]): AssetStats {
  const byCategory: Record<AssetCategory, number> = {
    branding: 0,
    documents: 0,
    decks: 0,
    notes: 0,
    images: 0,
    other: 0,
  };

  let totalSizeBytes = 0;
  let withFile = 0;

  for (const asset of assets) {
    byCategory[asset.category]++;
    if (asset.fileSize) totalSizeBytes += asset.fileSize;
    if (asset.storageId) withFile++;
  }

  return {
    total: assets.length,
    byCategory,
    totalSizeBytes,
    withFile,
  };
}

// ── Inline content helpers ──────────────────────────────────────

const INLINE_CATEGORIES: Set<AssetCategory> = new Set(["notes", "documents"]);

/** Returns true when this asset holds inline text content (notes/docs) */
export function isInlineContent(asset: Asset): boolean {
  return INLINE_CATEGORIES.has(asset.category) && !!asset.content;
}

/** Truncate text with ellipsis, useful for card previews */
export function truncateContent(text: string | undefined, maxLength = 120): string {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "…";
}
